import React from 'react';
import SEO from './components/SEO';
import { getCompetitionSchema } from './components/CompetitionSchema';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import GoogleDriveVideo from './components/GoogleDriveVideo';

const sampleProblemsUrl = "https://www.immchallenge.org/Pages/Sample.html";
const pastSolutionsUrl = "https://www.immchallenge.org/Contests/index.html";
const registrationFormUrl = "https://pakistanmathteam.com/";

export default function PMMCPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const rotatingImages = ['/IMG_8240 2.png', '/unnamed.jpg'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % rotatingImages.length);
    }, 4000); // Change rotating image every 4 seconds
    
    return () => clearInterval(interval);
  }, []);

  // SEO Data for PMMC page
  const competitionData = {
    name: "Pakistan Mathematical Modeling Challenge (PMMC)",
    desc: "The official National Competition designed to serve as the qualifying stage for the International Mathematical Modeling Challenge (IMMC)",
    startDate: "2025-01-01",
    endDate: "2025-12-31"
  };

  const seoData = {
    title: "Pakistan Mathematical Modeling Challenge",
    description: "Join the Pakistan Mathematical Modeling Challenge (PMMC) - the official qualifying competition for IMMC. Register now for 2025 and represent Pakistan internationally.",
    keywords: "Pakistan Mathematical Modeling Challenge, PMMC registration, IMMC Pakistan, mathematical modeling competition, Pakistan math team, IMMC 2025, mathematical modeling challenge Pakistan, PMMC 2025 registration",
    url: "https://capstonepakistan.org/competitions/pmmc",
    structuredData: getCompetitionSchema(competitionData)
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-red-500">
            <div className="absolute inset-0 bg-black/10"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center">
              <img 
                src="/pmmclogo.png" 
                alt="Pakistan Mathematical Modeling Challenge PMMC Logo" 
                className="w-32 h-32 object-contain rounded-full mx-auto mb-8 border-4 border-white shadow-2xl bg-white" 
                loading="lazy"
              />
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Pakistan Mathematical Modeling Challenge
              </h1>
              <h2 className="text-2xl lg:text-3xl font-semibold mb-6 text-white/90">IMMC | Pakistan Math Team Selection</h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-4xl mx-auto">
                The official National Competition designed to serve as the qualifying stage for the International Mathematical Modeling Challenge (IMMC)
              </p>
            </div>
          </div>
        </section>

        {/* Celebrating Our Success Section */}
        <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrating Our Success
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Witness the moments that define our journey towards academic excellence
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left side - Rotating Image */}
              <div className="relative">
                <div className="relative w-full h-[400px] lg:h-[500px] overflow-hidden rounded-[20px] shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 bg-white">
                  {rotatingImages.map((image, index) => (
                    <img 
                      key={index}
                      src={image} 
                      alt={`Capstone Pakistan Success Story ${index + 1} - Academic Excellence Achievement`} 
                      className={`absolute inset-0 w-full h-full object-cover rounded-[20px] transition-all duration-1000 ease-in-out transform ${
                        index === currentImageIndex 
                          ? 'opacity-100 scale-100 rotate-0' 
                          : 'opacity-0 scale-110 rotate-3'
                      }`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ))}
                  
                  {/* Overlay with success indicators - only PMMC element */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-[20px]">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center animate-pulse" style={{animationDelay: '0.5s'}}>
                          <span className="text-white text-xs font-bold">PMMC</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - Content */}
              <div className="text-center lg:text-left">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Building Tomorrow's Leaders
                </h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Our competitions provide students with unique opportunities to develop critical thinking, 
                  problem-solving skills, and international exposure. Through rigorous challenges and 
                  collaborative learning, we prepare the next generation of innovators and leaders.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse"></div>
                    <span className="text-lg text-gray-700">International Recognition</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <span className="text-lg text-gray-700">Academic Excellence</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-purple-600 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                    <span className="text-lg text-gray-700">Global Networking</span>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a 
                    href={registrationFormUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 bg-blue-700 hover:bg-blue-800 text-white font-semibold text-lg rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Register for PMMC
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-[20px] p-8 lg:p-12 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 text-center">About PMMC</h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  The Pakistan Mathematical Modeling Challenge is the official National Competition designed to serve as the qualifying stage for the International Mathematical Modeling Challenge (IMMC) organized by The Consortium for Mathematics and Its Applications.
                </p>
                <p>
                  Following the competition, the top participants will be selected and appointed as members of the Pakistan Mathematics Team. This team will be representing Pakistan in the IMMC 2025. Participants' performance in the competition will be evaluated for recognition, which may include distinctions such as Honorable Mention, Meritorious, or Outstanding based on their solutions.
                </p>
              </div>
            </div>
          </div>
        </section>



        {/* Competition Details Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Competition Format</h3>
                <div className="space-y-4 text-gray-700">
                  <p>The format of the competition is online. A team of up to 4 students enrolled in the same school may register.</p>
                  <p>The competition problem will be dispatched to all registered teams by the 15th of December 2024. Each team must submit the solution by their respective deadline.</p>
                  <p>Top participants will be selected for the Pakistan Math Team and their solutions will be sent to the IMMC committee for evaluation.</p>
                </div>
              </div>
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Eligibility</h3>
                <div className="space-y-4 text-gray-700">
                  <p>Students must be enrolled in School Year 8-13/O Levels/A Level/Higher Secondary Education.</p>
                  <p>All students who have not yet enrolled in a university are eligible to participate.</p>
                  <p>Both individuals and teams can be selected for the Pakistan Math Team.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* National Teams Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                2024 National Teams
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Meet the teams representing Pakistan at the International Mathematical Modeling Challenge
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center">
                <img src="/WhatsApp Image 2024-09-27 at 8.03.39 AM (2).png" alt="Pakistan Math Team Member" className="w-64 h-64 object-cover rounded-2xl mb-6 border-4 border-white shadow-lg mx-auto" />
                <div className="font-bold text-2xl mb-2 text-gray-900">Pakistan Math Team A</div>
                <div className="text-gray-600 text-lg">Representing Pakistan at IMMC</div>
              </div>
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center">
                <img src="/WhatsApp Image 2024-09-27 at 8.03.39 AM.png" alt="Pakistan Math Team Member" className="w-64 h-64 object-cover rounded-2xl mb-6 border-4 border-white shadow-lg mx-auto" />
                <div className="font-bold text-2xl mb-2 text-gray-900">Pakistan Math Team B</div>
                <div className="text-gray-600 text-lg">Representing Pakistan at IMMC</div>
              </div>
            </div>
          </div>
        </section>

        {/* More About PMMC Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                More About PMMC
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Additional highlights and behind-the-scenes footage from the Pakistan Mathematical Modeling Challenge
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <div className="mb-6">
                  <GoogleDriveVideo
                    fileId="1l_HQJePJ_YFzLruzWUqnP1B29qU2-3sZ"
                    title="PMMC Highlights"
                    className="w-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">PMMC Highlights</h3>
                <p className="text-gray-600 leading-relaxed">
                  Watch the highlights from the Pakistan Mathematical Modeling Challenge and see our winning team that represented Pakistan on the world stage.
                </p>
              </div>
              
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <div className="aspect-video mb-6 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <div className="text-center text-white">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <h4 className="text-xl font-semibold mb-2">Follow PMMC on Instagram</h4>
                    <p className="text-sm opacity-90">For more exclusive content and updates</p>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Stay Connected</h3>
                <p className="text-gray-600 leading-relaxed">
                  Follow our Instagram page for exclusive behind-the-scenes content, student spotlights, and real-time updates from PMMC.
                </p>
                <div className="mt-4">
                  <a 
                    href="https://www.instagram.com/pmmcofficial/?hl=en" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg transition-all duration-200 hover:scale-105"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Follow @pmmcofficial
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Instagram Section */}
        <section className="py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">Follow PMMC on Instagram</h2>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Stay connected with the latest updates, behind-the-scenes moments, and student achievements from the Pakistan Mathematical Modeling Challenge
              </p>
            </div>

            <div className="text-center">
              <a 
                href="https://www.instagram.com/pmmcofficial/?hl=en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Follow @pmmcofficial
              </a>
            </div>
          </div>
        </section>

        {/* Rewards & Affiliations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Rewards</h3>
                <div className="space-y-4 text-gray-700">
                  <p>After the international round, solutions will be categorized as Successful Participant, Honorable Mention, Meritorious, or Outstanding.</p>
                  <p>Outstanding teams will have their Solution Papers published online and receive national/international recognition.</p>
                </div>
              </div>
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Affiliations</h3>
                <div className="space-y-2 text-gray-700">
                  <p>COMAP (USA), National Institute of Education (Singapore), Tongji University (China), NeoUnion ESC Organization (Hong Kong), University of Hong Kong, Moscow State University (Russia), Freudenthal Institute (Netherlands), Roskilde University (Denmark), Australian Council for Educational Research (Australia), University of Massachusetts Lowell (USA)</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PMMC Video Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                PMMC in Action
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Watch exclusive footage from the Pakistan Mathematical Modeling Challenge
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <div className="mb-6">
                  <GoogleDriveVideo
                    fileId="1fkqNiIXHligto4vkp6nTChIn4bJrBvBC"
                    title="PMMC in Action"
                    className="w-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">PMMC Highlights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Watch the highlights from the Pakistan Mathematical Modeling Challenge.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                Get Started
              </h2>
              <p className="text-xl lg:text-2xl text-black leading-relaxed max-w-3xl mx-auto">
                Everything you need to participate in PMMC 2025
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              <a 
                href={registrationFormUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-white rounded-[20px] px-8 py-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div className="font-bold text-xl text-gray-900 mb-2">Registration Form</div>
                <div className="text-gray-600">Register for PMMC 2025</div>
              </a>
              <a 
                href={sampleProblemsUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-white rounded-[20px] px-8 py-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div className="font-bold text-xl text-gray-900 mb-2">Sample Problems</div>
                <div className="text-gray-600">Practice with examples</div>
              </a>
              <a 
                href={pastSolutionsUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-white rounded-[20px] px-8 py-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                  </svg>
                </div>
                <div className="font-bold text-xl text-gray-900 mb-2">Past Solutions</div>
                <div className="text-gray-600">Learn from winners</div>
              </a>
              <a 
                href="https://pakistanmathteam.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-white rounded-[20px] px-8 py-6 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400 transform hover:scale-105 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <div className="font-bold text-xl text-gray-900 mb-2">Campus Ambassador</div>
                <div className="text-gray-600">Join our program</div>
              </a>
            </div>
          </div>
        </section>

        {/* Important Information Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Registration Guide */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Registration Guide</h3>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p>Students from each group (consisting of 4 or less students enrolled in the same school) must register for the competition individually or together and enter the name of their group leader.</p>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                      <span><strong>Individual Registration:</strong> Fill out the registration form, enter your own name as group leader, and pay PKR 2,500.</span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                      <span><strong>Team Registration:</strong> Each member fills the form, all use the same group leader name. Payment can be made individually or collectively (PKR 2,500 per student).</span>
                    </div>
                  </div>
                  <p className="font-semibold">Confirmation will be sent within 48 hours. Registration deadline: <span className="text-red-600">15th December 2024</span>.</p>
                </div>
              </div>
              
              {/* Important Dates */}
              <div className="bg-white rounded-[20px] p-8 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Important Dates</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700"><strong>Registration Deadline:</strong> 15th December 2024</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700"><strong>Competition Dates:</strong> February to April 2025</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700"><strong>Pakistan Math Team Announcement:</strong> 20th April 2025</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <span className="text-gray-700"><strong>Final Result:</strong> June 2025</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Campus Ambassador Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                Campus Ambassador Program
              </h2>
              <p className="text-xl lg:text-2xl text-black leading-relaxed max-w-3xl mx-auto">
                Join our program and help promote academic excellence in your school!
              </p>
            </div>
            
            <div className="bg-white rounded-[20px] p-8 lg:p-12 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border-r-4 border-b-4 border-teal-400">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-black mb-6">What You'll Do</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-black">Promote PMMC and other competitions in your school</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-black">Help organize information sessions and workshops</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-black">Assist students with registration and preparation</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-black">Gain leadership experience and recognition</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-black">Receive certificates and potential scholarships</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <a 
                    href="https://pakistanmathteam.com/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-block px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-lg rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-4xl lg:text-6xl font-bold text-black mb-6 leading-tight">
              Get in Touch
            </h2>
            <p className="text-xl lg:text-2xl text-black leading-relaxed max-w-3xl mx-auto mb-12">
              Have questions? We're here to help you succeed in PMMC 2025
            </p>
            <a 
              href="mailto:admin@capstonepakistan.org" 
              className="inline-block px-8 py-4 bg-white hover:bg-gray-100 text-black font-semibold text-lg rounded-lg border-2 border-gray-900 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Contact Us
            </a>
          </div>
        </section>
      </div>
    </>
  );
} 